import type { EntryBounds, EntryData, EntryKind, EntryOverride, EntryRule } from './revenueEntryApi';

/** Названия параметров - как в журнале действий (services/revenueEntryRules.js) */
export const KIND_LABELS: Record<EntryKind, string> = {
  extend: 'Продление на сегодня',
  unlock: 'Разблокировка таблицы',
  date: 'Дата ввода'
};

export const SCHEDULE_LABELS = {
  open: 'Открытие ввода',
  close: 'Закрытие ввода'
};

/** Что делает правило: подсказка в форме */
export const KIND_HINTS: Record<EntryKind, string> = {
  extend: 'Текущая дата ввода остается открытой до указанного срока, в том числе после открытия следующего дня.',
  unlock: 'Ввод открыт независимо от времени, дата ввода определяется автоматически.',
  date: 'Вводятся данные за выбранную дату, время окна не учитывается. По истечении срока ввод вернется к автоматической дате.'
};

export const DATE_BACK_MONTHS = 6;
export const OVERRIDES_PER_PAGE = 50;

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MOMENT_RE = /^(\d{4}-\d{2}-\d{2})[ T]([01]\d|2[0-3]):([0-5]\d)$/;
const TYPED_DAY_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;

function isValidDay(value: string): boolean {
  const match = DAY_RE.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Момент 'YYYY-MM-DD HH:MM' (или с T) -> миллисекунды «настенного» времени Алматы; null, если строка неверная */
function wallMs(value: string | null | undefined): number | null {
  const match = MOMENT_RE.exec(String(value || '').trim());
  if (!match || !isValidDay(match[1])) return null;
  const [y, m, d] = match[1].split('-').map(Number);
  return Date.UTC(y, m - 1, d, Number(match[2]), Number(match[3]));
}

/** 'HH:MM' -> минуты от полуночи; null, если строка неверная */
export function timeMinutes(value: string): number | null {
  const match = TIME_RE.exec(value.trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/** 'YYYY-MM-DD' -> 'DD.MM.YYYY' */
export function formatDay(ymd: string | null): string {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}.${m}.${y}`;
}

/** 'YYYY-MM-DD HH:MM' -> 'DD.MM.YYYY HH:MM' */
export function formatMoment(value: string | null | undefined): string {
  if (!value) return '';
  const [day, time] = value.replace('T', ' ').split(' ');
  return `${formatDay(day)} ${time || ''}`.trim();
}

/** Значение правила; совпадает с текстом журнала «было -> стало» */
export function ruleText(kind: EntryKind, rule: Pick<EntryRule, 'date' | 'until'> | null): string {
  if (!rule) return 'нет';
  const until = `до ${formatMoment(rule.until)}`;
  if (kind === 'extend') return `ввод за ${formatDay(rule.date)} ${until}`;
  if (kind === 'date') return `${formatDay(rule.date)} ${until}`;
  return until;
}

/** Значение исключения аптеки без срока (срок - отдельной колонкой) */
export function overrideValue(item: EntryOverride): string {
  if (item.kind === 'extend') return `ввод за ${formatDay(item.date)}`;
  if (item.kind === 'date') return formatDay(item.date);
  return 'ввод открыт';
}

/** Время закрытия ввода: всегда на следующий день, при совпадении с открытием блокировки нет */
export function closeText(open: string, close: string): string {
  return open === close ? `${close} следующего дня, без блокировки` : `${close} следующего дня`;
}

/** Момент сервера -> значение поля datetime-local */
export function toInputMoment(value: string): string {
  return value.replace(' ', 'T');
}

/** Значение поля datetime-local -> момент для сервера */
export function fromInputMoment(value: string): string {
  return value.replace('T', ' ');
}

/** Окно сети до отправки; сервер повторяет проверку. Пустая строка - ошибок нет */
export function validateSchedule(open: string, close: string): string {
  const openAt = timeMinutes(open);
  const closeAt = timeMinutes(close);
  if (openAt === null) return 'Укажите время открытия ввода';
  if (closeAt === null) return 'Укажите время закрытия ввода';
  if (closeAt > openAt) return 'Закрытие (на следующий день) должно быть не позже открытия: иначе будут открыты две даты сразу';
  return '';
}

/** Срок правила: позже текущего момента, не дольше 24 часов; продление - позже обычного закрытия */
export function validateUntil(value: string, bounds: EntryBounds, kind: EntryKind): string {
  const until = wallMs(value);
  if (until === null) return 'Укажите срок действия: дату и время';
  if (until <= (wallMs(bounds.now) || 0)) return 'Срок действия должен быть позже текущего времени';
  if (until > (wallMs(bounds.maxUntil) || 0)) return `Срок действия - не больше 24 часов, не позже ${formatMoment(bounds.maxUntil)}`;
  if (kind === 'extend' && until <= (wallMs(bounds.extendAfter) || 0)) {
    return `Продление должно заканчиваться позже обычного закрытия (${formatMoment(bounds.extendAfter)})`;
  }
  return '';
}

/** Дата ввода: за последние 6 месяцев, не позже сегодня */
export function validateDate(value: string, bounds: EntryBounds): string {
  if (!isValidDay(value)) return 'Укажите дату ввода';
  if (value > bounds.maxDate) return 'Дата ввода не может быть позже сегодняшней';
  if (value < bounds.minDate) return `Дата ввода - не раньше ${formatDay(bounds.minDate)} (последние ${DATE_BACK_MONTHS} месяцев)`;
  return '';
}

/** Набранная вручную дата 'DD.MM.YYYY' -> 'YYYY-MM-DD'; null, если она неверная */
export function parseTypedDay(text: string): string | null {
  const match = TYPED_DAY_RE.exec(text.trim());
  if (!match) return null;
  const ymd = `${match[3]}-${match[2]}-${match[1]}`;
  return isValidDay(ymd) ? ymd : null;
}

/** Продление сейчас невозможно: обычное закрытие текущей даты позже предела в 24 часа */
export function extendBlocked(bounds: EntryBounds): boolean {
  return (wallMs(bounds.extendAfter) || 0) >= (wallMs(bounds.maxUntil) || 0);
}

/** Подпись аптеки - как в выборе аптеки отчетности */
export function pharmacyLabel(item: { username: string; pharmacyNum: string }): string {
  return `${item.username} (аптека ${item.pharmacyNum})`;
}

/** Подпись аптеки исключения: логин аптеки хранится в поле pharmacy */
export function overridePharmacyLabel(item: EntryOverride): string {
  return pharmacyLabel({ username: item.pharmacy, pharmacyNum: item.pharmacyNum });
}

export function filterOverrides(items: EntryOverride[], query: string): EntryOverride[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return items;
  return items.filter((item) =>
    [item.pharmacyNum, item.pharmacy, item.fullName || ''].some((text) => String(text).toLowerCase().indexOf(needle) >= 0)
  );
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / OVERRIDES_PER_PAGE));
}

export function pageSlice(items: EntryOverride[], page: number): EntryOverride[] {
  return items.slice((page - 1) * OVERRIDES_PER_PAGE, page * OVERRIDES_PER_PAGE);
}

/**
 * Через сколько миллисекунд данные экрана устареют: ближайший срок правила, закрытие или открытие ввода.
 * loadedAt - Date.now() в момент получения данных (bounds.now - время сервера в тот же момент). null - перечитывать незачем.
 */
export function refreshDelay(data: EntryData, loadedAt: number): number | null {
  const serverNow = wallMs(data.bounds.now);
  if (serverNow === null) return null;
  const moments: Array<string | null | undefined> = [data.state.until, data.state.opensAt];
  (['extend', 'unlock', 'date'] as EntryKind[]).forEach((kind) => {
    const rule = data.network[kind];
    if (rule) moments.push(rule.until);
  });
  data.overrides.forEach((item) => moments.push(item.until));

  const elapsed = Date.now() - loadedAt;
  let nearest: number | null = null;
  moments.forEach((value) => {
    const at = wallMs(value);
    if (at === null) return;
    // Секунда запаса: срок на сервере проверяется с точностью до секунды
    const delay = at - serverNow - elapsed + 1000;
    if (delay > 0 && (nearest === null || delay < nearest)) nearest = delay;
  });
  return nearest;
}
